<template>
    <div>
        <el-button id="concordance-search-button"
                   class="concordance-search-button" 
                   type="primary" 
                   @click="openConcordanceDialog">
            {{ $t('segmentSearch.concordanceSearch') }}
        </el-button>

        <el-dialog v-model="concordanceDialogVisible"
                   :title="$t('segmentSearch.concordanceSearch')"
                   width="1400px"
                   class="concordance-dialog"
                   :close-on-click-modal="true"
                   :close-on-press-escape="true">
            <div class="concordance-content">
                <div class="concordance-layout">
                    <div class="concordance-filters">
                        <p class="concordance-description">
                        {{ $t('segmentSearch.concordanceSearchDescription') }}
                        </p>
                        <div class="concordance-inputs">
                            <div id="concordance-source-input"
                                 class="concordance-input-group">
                                <label class="concordance-label">
                                {{ $t('segmentSearch.source') }}
                                </label>
                                <el-input v-model="concordanceSourceText"
                                          :placeholder="$t('segmentSearch.concordanceSearchPlaceholder')"
                                          class="concordance-search-input"
                                          clearable>
                                    <template #prefix>
                                        <span class="material-symbols-outlined search-icon">search</span>
                                    </template>
                                </el-input>
                            </div>
                            <div id="concordance-target-input"
                                 class="concordance-input-group">
                                <label class="concordance-label">
                                {{ $t('segmentSearch.target') }}
                                </label>
                                <el-input v-model="concordanceTargetText"
                                          :placeholder="$t('segmentSearch.concordanceSearchPlaceholder')"
                                          class="concordance-search-input"
                                          clearable>
                                    <template #prefix>
                                        <span class="material-symbols-outlined search-icon">search</span>
                                    </template>
                                </el-input>
                            </div>
                        </div>
                        <el-button id="concordance-search-btn"
                                   class="concordance-search-btn"
                                   type="primary" 
                                   @click="performConcordanceSearch"
                                   :loading="isLoading">
                            {{ $t('segmentSearch.search') }}
                        </el-button>
                    </div>
                    <div id="concordance-results"
                         class="concordance-results" 
                         v-loading="isLoading" 
                         ref="concordanceResults">
                        <div v-if="searchResults.length === 0 && !isLoading"
                             class="no-results">
                            {{ $t('concordanceSearch.noResults') }}
                        </div>
                        <ConcordanceSearchResultItem
                            v-for="(result, index) in searchResults"
                            :key="index"
                            :result="result"
                            :search-source-text="concordanceSourceText"
                            :search-target-text="concordanceTargetText"
                        />
                        <div v-if="hasMore && !isLoading" 
                             class="load-more-container">
                            <el-button class="load-more-btn" 
                                       @click="loadMore" 
                                       :loading="isLoadingMore">
                                {{ $t('concordanceSearch.loadMore') }}
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <el-button @click="concordanceDialogVisible = false">
                    {{ $t('generic.cancel') }}
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { ConcordanceSearchResult } from './ConcordanceSearchModels';
    import { concordanceSearch } from './segmentSearchService';
    import ConcordanceSearchResultItem from './ConcordanceSearchResultItem.vue';
    import { TranslationConfiguration } from '@/models/TranslationConfiguration';
    import { translationConfigService } from '@/features/TranslationConfig/translationConfigService';
    import { ConfigurationOptions } from '@/models/ConfigurationOptions';

    export default defineComponent({
        name: 'ConcordanceSearch',
        components: {
            ConcordanceSearchResultItem
        },
        props: {
            translationConfig: {
                type: Object as PropType<TranslationConfiguration | null>,
                default: null
            }
        },
        data() {
            return {
                concordanceDialogVisible: false,
                concordanceSourceText: '',
                concordanceTargetText: '',
                searchResults: [] as ConcordanceSearchResult[],
                isLoading: false,
                isLoadingMore: false,
                hasMore: false,
                currentPage: 1,
                pageSize: 10,
                configService: translationConfigService.getInstance(),
                configOptions: null as ConfigurationOptions | null
            };
        },
        computed: {
            projectCodeId(): number | undefined {
                if (!this.translationConfig?.projectCode || !this.configOptions) {
                    return undefined;
                }
            
                const project = this.configOptions.projectCodes.find(
                    p => p.code === this.translationConfig?.projectCode
                );
            
                return project && project.id !== -1 ? project.id : undefined;
            }
        },
        async mounted() {
            this.configOptions = await this.configService.getConfigOptions();
            document.addEventListener('copy', this.handleCopy);
        },
        beforeUnmount() {
            document.removeEventListener('copy', this.handleCopy);
        },
        methods: {
            handleCopy(event: ClipboardEvent) {
                const selection = window.getSelection();
                if (!selection || selection.rangeCount === 0) {
                    return;
                }

                const range = selection.getRangeAt(0);
                const container = range.commonAncestorContainer;
                const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as Element;

                if (element && element.closest('.concordance-results')) {
                    event.preventDefault();
                    const plainText = selection.toString();
                    event.clipboardData?.setData('text/plain', plainText);
                }
            },
            getSelectedTextWithSource(): { text: string; isSource: boolean | null } {
                const selection = window.getSelection();
                if (!selection || selection.rangeCount === 0) return { text: '', isSource: null };

                const text = selection.toString().trim();
                if (!text) return { text: '', isSource: null };

                const range = selection.getRangeAt(0);
                const container = range.commonAncestorContainer;
                const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as Element;

                if (!element) return { text: text.split('\n')[0].trim(), isSource: null };

                const isFromSource = !!element.closest('.source-text');
                const isFromTarget = !!element.closest('#tareget-container');

                let isSource: boolean | null = null;
                if (isFromSource) {
                    isSource = true;
                } else if (isFromTarget) {
                    isSource = false;
                }

                return { text: text.split('\n')[0].trim(), isSource };
            },
            openConcordanceDialog() {
                const { text, isSource } = this.getSelectedTextWithSource();

                this.concordanceSourceText = '';
                this.concordanceTargetText = '';
                this.searchResults = [];
                this.hasMore = false;
                this.currentPage = 1;

                if (isSource === true) {
                    this.concordanceSourceText = text;
                } else if (isSource === false) {
                    this.concordanceTargetText = text;
                }

                this.concordanceDialogVisible = true;

                if (this.concordanceSourceText || this.concordanceTargetText) {
                    this.performConcordanceSearch();
                }
            },
            async performConcordanceSearch() {
                if (!this.concordanceSourceText && !this.concordanceTargetText) {
                    return;
                }

                this.isLoading = true;
                this.currentPage = 1;
                this.searchResults = [];

                try {
                    const response = await concordanceSearch({
                        sourceText: this.concordanceSourceText,
                        targetText: this.concordanceTargetText,
                        page: this.currentPage,
                        pageSize: this.pageSize,
                        subjectId: this.translationConfig?.subjectId,
                        subSubjectId: this.translationConfig?.subSubjectId,
                        sourceLanguageId: this.translationConfig?.sourceLanguageId,
                        targetLanguageId: this.translationConfig?.targetLanguageId,
                        projectCodeId: this.projectCodeId
                    });

                    this.searchResults = response.results;
                    this.hasMore = response.hasMore;
                } catch (error) {
                    console.error('Concordance search error:', error);
                } finally {
                    this.isLoading = false;
                }
            },
            async loadMore() {
                this.isLoadingMore = true;
                this.currentPage++;

                try {
                    const response = await concordanceSearch({
                        sourceText: this.concordanceSourceText,
                        targetText: this.concordanceTargetText,
                        page: this.currentPage,
                        pageSize: this.pageSize,
                        subjectId: this.translationConfig?.subjectId,
                        subSubjectId: this.translationConfig?.subSubjectId,
                        sourceLanguageId: this.translationConfig?.sourceLanguageId,
                        targetLanguageId: this.translationConfig?.targetLanguageId,
                        projectCodeId: this.projectCodeId
                    });

                    this.searchResults = [...this.searchResults, ...response.results];
                    this.hasMore = response.hasMore;
                } catch (error) {
                    console.error('Load more error:', error);
                    this.currentPage--;
                } finally {
                    this.isLoadingMore = false;
                }
            }
        }
    });
</script>

<style lang="scss" scoped>
    .concordance-search-button {
        background-color: var(--yellow);
        border-color: var(--yellow);
        color: var(--dark-blue);
        font-weight: 600;

        &:hover {
            opacity: 0.9;
        }
    }

    .concordance-dialog {
        :deep(.el-dialog__header) {
            background-color: var(--dark-blue);
            color: var(--text-color);
            padding: 16px 20px;
            margin: 0;
        }

        :deep(.el-dialog__title) {
            color: var(--text-color);
            font-weight: 600;
        }

        :deep(.el-dialog__headerbtn .el-dialog__close) {
            color: var(--text-color);
        }

        :deep(.el-dialog__body) {
            padding: 20px;
        }

        .concordance-content {
            min-height: 400px;

            .concordance-layout {
                display: flex;
                gap: 24px;
                height: 100%;
            }

            .concordance-filters {
                flex: 0 0 450px;
                display: flex;
                flex-direction: column;
            }

            .concordance-description {
                margin: 0 0 16px 0;
                color: var(--dark-blue);
                font-size: 14px;
            }

            .concordance-results {
                flex: 1;
                background-color: var(--light-blue);
                border-radius: 8px;
                padding: 16px;
                overflow-y: auto;
                max-height: 400px;

                .no-results {
                    text-align: center;
                    color: var(--dark-blue);
                    opacity: 0.7;
                    padding: 40px 20px;
                    font-size: 14px;
                }

                .load-more-container {
                    display: flex;
                    justify-content: center;
                    margin-top: 16px;

                    .load-more-btn {
                        background-color: var(--dark-blue);
                        border-color: var(--dark-blue);
                        color: white;
                        font-weight: 600;

                        &:hover {
                            opacity: 0.9;
                        }
                    }
                }
            }

            .concordance-inputs {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .concordance-input-group {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .concordance-label {
                font-weight: 600;
                color: var(--dark-blue);
                font-size: 14px;
            }

            .concordance-search-btn {
                margin-top: 24px;
                background-color: var(--yellow);
                border-color: var(--yellow);
                color: var(--dark-blue);
                font-weight: 600;
                width: 100%;

                &:hover {
                    opacity: 0.9;
                }
            }

            .concordance-search-input {
                :deep(.el-input__wrapper) {
                    background-color: var(--light-blue);
                    box-shadow: none;
                    border-radius: 8px;
                    padding: 12px 16px;
                }

                :deep(.el-input__inner) {
                    color: var(--dark-blue);
                    font-size: 16px;

                    &::placeholder {
                        color: var(--dark-blue);
                        opacity: 0.7;
                    }
                }

                :deep(.el-input__wrapper:hover) {
                    box-shadow: none;
                }

                :deep(.el-input__wrapper.is-focus) {
                    box-shadow: 0 0 0 2px rgba(52, 74, 109, 0.2);
                }
            }
        }
    }
</style>
